import { makeAutoObservable } from 'mobx';
import { S3Url } from 's3u';
import { z } from 'zod';
import extractValue, {
  type EventOrValue,
} from '../../../../../store/utils/extractValue';
import Validator from '../../../../../store/utils/Validator';
import type Cdn from '../../../../account/store/services/Cdn';

export default class VersionFile {
  static Schema = z
    .object({
      url: z.url(),
      variation: z.string(),
      cdnSettings: z.any(),
      existenceState: z.any(),
      existenceError: z.any(),
    })
    .superRefine((data, ctx) => {
      const message = validateFileUrl(data);
      if (message) {
        ctx.addIssue({ code: 'custom', path: ['url'], message });
      }
    });

  id!: number;
  url = '';
  variation = '';

  existenceState: ExistenceState = 'not-checked';
  existenceError = '';
  cdnSettings?: Cdn;

  isDeleted = false;

  private readonly validator = new Validator(this, VersionFile.Schema);

  constructor(
    dto: VersionFileConstructorDto = {},
    private readonly onChange?: (file: VersionFile) => void,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    Object.assign(this, dto);
  }

  get errors() {
    return this.validator.errorsForEnabledFields;
  }

  get isEmpty(): boolean {
    return !this.url;
  }

  get isChecking(): boolean {
    if (this.isEmpty) {
      return false;
    }

    return ['checking', 'not-checked'].includes(this.existenceState);
  }

  get isValid(): boolean {
    return this.validator.isValid;
  }

  get name() {
    return getUrlShortName(this.url);
  }

  delete() {
    this.isDeleted = true;
    this.onChange?.(this);
  }

  handleChange(e: EventOrValue) {
    const { name, value } = extractValue(e);

    if (name === 'url' && this.url !== value) {
      this.existenceState = 'checking';
    }

    Object.assign(this, { [name]: value });

    this.validator.enableFieldValidation(name as keyof VersionFile);
    this.validator.validate();
    this.onChange?.(this);
  }

  handleBlur(e: EventOrValue) {
    const { name } = extractValue(e);
    this.validator.enableFieldValidation(name as keyof VersionFile);
  }

  getLabel(index: number): string {
    if (!this.url) {
      return 'Add new file URL';
    }

    const indexedName = `URL ${index + 1}`;

    if (this.variation) {
      return `${indexedName} (${this.variation})`;
    }

    return indexedName;
  }

  toJSON() {
    return {
      url: this.url,
      variation: this.variation || undefined,
    };
  }

  setExistenceState(value: ExistenceState, error = '') {
    this.existenceState = value;
    this.existenceError = error;

    this.validator.enableFieldValidation('url');
    this.validator.validate();
    this.onChange?.(this);
  }

  addCdnSettings(cdn: Cdn) {
    this.cdnSettings = cdn;
  }
}

function validateFileUrl(file: {
  cdnSettings?: Cdn;
  existenceError?: string;
  existenceState?: ExistenceState;
  url: string;
}): string | undefined {
  const s3Url = new S3Url(file.url || '');

  if (!s3Url.isValid) {
    return 'valid URL required';
  }

  if (!s3Url.bucket) {
    return "can't extract bucket name from url";
  }

  if (file?.cdnSettings?.enabled) {
    const cdn = file.cdnSettings;

    if (cdn.s3Provider !== s3Url.provider?.id) {
      return `Unable to use CDN. Your CDN service is "${cdn.s3Provider}", while this file is on "${s3Url.provider?.id}"`;
    }

    if (cdn.bucket !== s3Url.bucket) {
      return `Unable to use CDN. Your CDN bucket is "${cdn.bucket}", while this file is on "${s3Url.bucket}"`;
    }
  }

  if (!s3Url.key) {
    return "can't get file path from url";
  }

  switch (file?.existenceState) {
    case 'exists': {
      return undefined;
    }

    case 'not-exists': {
      return file.existenceError || 'File not found in your Storage';
    }

    default: {
      return 'File is checking...';
    }
  }
}

function getUrlShortName(url: string) {
  const [, match = null] = url?.match(/\/([^#/?]+)[^/]*$/) || [];
  return match || url;
}

export interface VersionFileDto {
  url?: string;
  variation?: string;
}

export interface VersionFileConstructorDto extends VersionFileDto {
  existenceState?: ExistenceState;
  id?: number;
}

export type ExistenceState =
  | 'checking'
  | 'exists'
  | 'not-checked'
  | 'not-exists';
